import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import { SettingsProps, PlanSettingsFormValues } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { Formik } from "formik";
import { planSettingsValidation } from "../../rules/settings";

const planOptions = [
  {
    value: "Basic",
    label: "Basic - $29/month",
    price: 29,
    features: [
      "Up to 1,000 inventory items",
      "Basic reporting",
      "2 user accounts",
      "Email support",
      "30-day transaction history",
    ],
  },
  {
    value: "Professional",
    label: "Professional - $99/month",
    price: 99,
    features: [
      "Up to 10,000 inventory items",
      "Advanced reporting & analytics",
      "10 user accounts",
      "Priority email & chat support",
      "1-year transaction history",
      "Batch operations",
      "Custom categories",
    ],
  },
  {
    value: "Enterprise",
    label: "Enterprise - $299/month",
    price: 299,
    features: [
      "Unlimited inventory items",
      "Custom reporting & API access",
      "Unlimited user accounts",
      "24/7 priority support",
      "Unlimited transaction history",
      "Advanced automation",
      "Multi-location support",
      "Custom integrations",
      "Dedicated account manager",
    ],
  },
];

const PlanSettings: React.FC<SettingsProps> = ({
  onGlobalCancel,
  isSaving,
  onSave,
}) => {
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(planOptions[1]);

  const toggleUpgradeModal = () => setUpgradeModal(!upgradeModal);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    React.useState("card");

  const handleSave = async (values: PlanSettingsFormValues) => {
    await onSave(async () => {
      console.log("Saving plan settings:", values);
      // TODO: Implement actual API call
    });
  };

  const billingCycleOptions: { value: string; label: string }[] = [
    { value: "monthly", label: "Monthly" },
    { value: "annually", label: "Annually (Save 20%)" },
  ];

  const paymentMethodOptions = [
    { value: "card", label: "Credit/Debit Card" },
    { value: "momo", label: "Mobile Money" },
    { value: "bank", label: "Bank Transfer" },
    { value: "crypto", label: "Cryptocurrency" },
  ];

  const CardPaymentForm = ({ formik }: { formik: any }) => (
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="billingName">Name on Card</Label>
          <Input
            type="text"
            id="billingName"
            name="billingName"
            placeholder="Enter name on card"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.billingName}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="cardNumber">Card Number</Label>
          <Input
            type="text"
            id="cardNumber"
            name="cardNumber"
            placeholder="XXXX XXXX XXXX XXXX"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.cardNumber}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="expiryDate">Expiry Date</Label>
          <Input
            type="text"
            id="expiryDate"
            name="expiryDate"
            placeholder="MM/YY"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.expiryDate}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="cvv">CVV</Label>
          <Input
            type="password"
            id="cvv"
            name="cvv"
            placeholder="***"
            maxLength={3}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.cvv}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  const MobileMoneyForm = ({ formik }: { formik: any }) => (
    <Row>
      <Col md={6}>
        <FormGroup>
          <Label for="phoneNumber">Phone Number</Label>
          <Input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            placeholder="Enter mobile money number"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.phoneNumber}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="provider">Provider</Label>
          <Select
            id="provider"
            name="provider"
            options={[
              { value: "mtn", label: "MTN Mobile Money" },
              { value: "airtel", label: "Airtel Money" },
              { value: "vodafone", label: "Vodafone Cash" },
            ]}
            onChange={(option) =>
              formik.setFieldValue("provider", option?.value)
            }
            value={formik.values.provider}
            onBlur={formik.handleBlur}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  const BankTransferForm = ({ formik }: { formik: any }) => (
    <Row>
      <Col md={12}>
        <FormGroup>
          <Label for="accountName">Account Name</Label>
          <Input
            type="text"
            id="accountName"
            name="accountName"
            placeholder="Enter account name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.accountName}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="accountNumber">Account Number</Label>
          <Input
            type="text"
            id="accountNumber"
            name="accountNumber"
            placeholder="Enter account number"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.accountNumber}
          />
        </FormGroup>
      </Col>
      <Col md={6}>
        <FormGroup>
          <Label for="bankName">Bank Name</Label>
          <Input
            type="text"
            id="bankName"
            name="bankName"
            placeholder="Enter bank name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.bankName}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  const CryptoPaymentForm = ({ formik }: { formik: any }) => (
    <Row>
      <Col md={12}>
        <FormGroup>
          <Label for="cryptoType">Cryptocurrency</Label>
          <Select
            id="cryptoType"
            name="cryptoType"
            options={[
              { value: "btc", label: "Bitcoin (BTC)" },
              { value: "eth", label: "Ethereum (ETH)" },
              { value: "usdt", label: "Tether (USDT)" },
            ]}
            onChange={(option) =>
              formik.setFieldValue("cryptoType", option?.value)
            }
            value={formik.values.cryptoType}
            onBlur={formik.handleBlur}
          />
        </FormGroup>
      </Col>
      <Col md={12}>
        <FormGroup>
          <Label for="walletAddress">Wallet Address</Label>
          <Input
            type="text"
            id="walletAddress"
            name="walletAddress"
            placeholder="Enter your wallet address"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.walletAddress}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  const initialValues: PlanSettingsFormValues = {
    billingCycle: "monthly",
    paymentMethod: "card",
    billingName: "",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    phoneNumber: "",
    provider: "",
    accountName: "",
    accountNumber: "",
    bankName: "",
    cryptoType: "",
    walletAddress: "",
    billingAddress: "",
    city: "",
    postalCode: "",
    selectedPlan: planOptions[1], // Default to Professional plan
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={planSettingsValidation}
      onSubmit={handleSave}
    >
      {(formik) => {
        const handleUpgrade = () => {
          if (!selectedPlan) return;
          formik.setFieldValue("selectedPlan", selectedPlan);
          toggleUpgradeModal();
        };

        return (
          <Form>
            <Modal
              isOpen={upgradeModal}
              toggle={toggleUpgradeModal}
              size="md"
              className="modal-dialog-centered"
            >
              <ModalHeader
                toggle={toggleUpgradeModal}
                className="border-bottom"
              >
                <h5 className="mb-0">Upgrade Your Plan</h5>
              </ModalHeader>
              <ModalBody className="p-4">
                <FormGroup>
                  <Label for="planSelect" className="h6">
                    Select New Plan
                  </Label>
                  <Select
                    id="planSelect"
                    options={planOptions}
                    value={planOptions.find(opt => opt.value === selectedPlan?.value)}
                    onChange={(option) => setSelectedPlan(option as typeof planOptions[0])}
                    defaultValue={planOptions[1]}
                    placeholder="Choose a plan..."
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: state.isFocused ? '#0d9488' : base.borderColor,
                        '&:hover': {
                          borderColor: '#0d9488'
                        },
                        boxShadow: state.isFocused ? '0 0 0 1px #0d9488' : base.boxShadow
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#0d9488' : state.isFocused ? '#f0fdfa' : 'white',
                        color: state.isSelected ? 'white' : '#134e4a',
                        ':hover': {
                          backgroundColor: state.isSelected ? '#0d9488' : '#f0fdfa'
                        }
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: 'white'
                      })
                    }}
                  />
                </FormGroup>
                {selectedPlan && (
                  <div className="mt-4">
                    <h6 className="mb-3">Plan Features:</h6>
                    <div className="bg-light p-3 rounded">
                      <ul className="list-unstyled mb-0">
                        {selectedPlan.features.map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="fas fa-check text-teal me-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div
                      className="alert alert-info mt-3"
                      style={{
                        backgroundColor: "#f0fdfa",
                        borderColor: "#0d9488",
                        color: "#134e4a",
                      }}
                    >
                      <i className="fas fa-info-circle me-2"></i>
                      You will be charged ${selectedPlan.price}/month
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter className="border-top">
                <Button color="secondary" onClick={toggleUpgradeModal}>
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: "#0d9488",
                    borderColor: "#0d9488",
                  }}
                  className="hover-effect"
                  onClick={handleUpgrade}
                  disabled={!selectedPlan}
                >
                  Confirm
                </Button>
              </ModalFooter>
            </Modal>
            <h5 className="mb-4">Plan & Billing</h5>

            {/* Current Plan Card */}
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-1">Current Plan: Professional</h6>
                    <p className="text-muted mb-0">
                      Your next billing date is July 1, 2025
                    </p>
                  </div>
                  <Button color="primary" outline onClick={toggleUpgradeModal}>
                    Upgrade Plan
                  </Button>
                </div>
                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: "75%" }}
                    aria-valuenow={75}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <small className="text-muted">75% of your storage used</small>
              </CardBody>
            </Card>

            {/* Form Fields */}
            <Row className="mb-4">
              <Col md={12}>
                <h6 className="mb-3">Billing Cycle</h6>
                <FormGroup>
                  <Select
                    id="billingCycle"
                    name="billingCycle"
                    options={billingCycleOptions}
                    defaultValue={billingCycleOptions[0]}
                    onChange={(option) =>
                      formik.setFieldValue("billingCycle", option?.value)
                    }
                    value={billingCycleOptions.find(
                      (opt) => opt.value === formik.values.billingCycle
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <h6 className="mb-3">Payment Method</h6>
                <FormGroup>
                  <Select
                    id="paymentMethod"
                    name="paymentMethod"
                    options={paymentMethodOptions}
                    defaultValue={paymentMethodOptions[0]}
                    onChange={(option) => {
                      const value = option?.value || "card";
                      setSelectedPaymentMethod(value);
                      formik.setFieldValue("paymentMethod", value);
                    }}
                    value={paymentMethodOptions.find(
                      (opt) => opt.value === formik.values.paymentMethod
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Dynamic Payment Form */}
            {selectedPaymentMethod === "card" && (
              <CardPaymentForm formik={formik} />
            )}
            {selectedPaymentMethod === "momo" && (
              <MobileMoneyForm formik={formik} />
            )}
            {selectedPaymentMethod === "bank" && (
              <BankTransferForm formik={formik} />
            )}
            {selectedPaymentMethod === "crypto" && (
              <CryptoPaymentForm formik={formik} />
            )}

            {/* Billing Address */}
            <Row className="mt-4">
              <Col md={12}>
                <h6 className="mb-3">Billing Address</h6>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label for="billingAddress">Street Address</Label>
                  <Input
                    type="textarea"
                    id="billingAddress"
                    name="billingAddress"
                    rows={2}
                    placeholder="Enter your billing address"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.billingAddress}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="city">City</Label>
                  <Input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="Enter city"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="postalCode">Postal Code</Label>
                  <Input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    placeholder="Enter postal code"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.postalCode}
                  />
                </FormGroup>
              </Col>
            </Row>

            <SettingsButtons
              onSave={formik.handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(formik.errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default PlanSettings;
